import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { isActiveEmp } from "../lib/constants";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Button } from "../components/ui/button";
import { BulkTools } from "./BulkTools";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Car, Bike, Plus, ArrowRightLeft, Undo2, Trash2, User, CheckCircle2, Loader2, PenLine, Printer, ChevronLeft, Upload, Download, FileDown, AlertTriangle, Pencil, Building2, Camera, X, CreditCard, Eye, KeyRound, History, Wrench,
} from "lucide-react";
import { FleetDialog } from "./rentals/RentalFleetTab";
import { api, fileUrl } from "../lib/api";
import { saveBlob } from "../lib/download";
import { useLang } from "../i18n/LanguageContext";
import { SignaturePad } from "./SignaturePad";
import { AssignAssetDialog } from "./AssignAssetDialog";
import { fileToDataUrl } from "./VehicleInspectionForm";
import { printVehicleReceipt } from "../lib/custodyReceipt";
import { isVehicleType, vehicleTypeLabel, getVehicleTypes, registerVehicleTypes } from "../lib/vehicles";
import { VehicleTypesManager, VehicleBulkBar } from "./VehicleTypeTools";
import { VehicleHistoryDialog } from "./VehicleHistoryDialog";
import { VehicleStatusBadge, VehicleStatusDialog, VehicleStatusManager, ST_COLORS } from "./VehicleStatus";
import { VehicleDowntimeReport } from "./VehicleDowntimeReport";

const today = () => new Date().toISOString().slice(0, 10);
const vIcon = (type) => (vehicleTypeLabel(type) === "سيارة" ? Car : Bike);
const MOTO_KINDS = ["صيني", "بوكسر"];
const isMoto = (type) => vehicleTypeLabel(type) === "دراجة نارية";

// Registration (رخصة السير) expiry status — computed from a Gregorian ISO date.
const regExpiryInfo = (iso) => {
  if (!iso) return null;
  const days = Math.ceil((new Date(iso + "T00:00:00") - new Date()) / 86400000);
  const state = days < 0 ? "expired" : days <= 30 ? "soon" : "valid";
  return { days, iso, state };
};

// ---------- Add vehicle dialog ----------
const normCard = (v) => String(v || "").trim();

// Shows how many vehicles share the same operating-card number (auto-counted) and, on click,
// the names of the riders holding those vehicles (كرت التشغيل المشترك).
const SharedCardField = ({ cardNumber, assets, currentVehicleId }) => {
  const [open, setOpen] = useState(false);
  const num = normCard(cardNumber);
  const sharers = useMemo(
    () => (num ? (assets || []).filter((a) => normCard(a.operating_card_number) === num) : []),
    [num, assets]
  );
  if (!num) return null;
  const selfIncluded = currentVehicleId ? sharers.some((s) => s.id === currentVehicleId) : false;
  const count = sharers.length + (selfIncluded ? 0 : 1);
  const shared = count > 1;
  return (
    <div className="space-y-1.5" data-testid="shared-card-field">
      <Label>كرت التشغيل المشترك (دبّاب)</Label>
      <button type="button" data-testid="shared-card-toggle" onClick={() => setOpen((o) => !o)}
        className={`w-full flex items-center justify-between rounded-lg border px-3 py-2 text-sm transition ${shared ? "border-amber-300 bg-amber-50 text-amber-800" : "border-slate-200 bg-white text-slate-600"}`}>
        <span className="flex items-center gap-1.5"><User className="h-4 w-4" /> عدد المناديب المشتركين في الكرت: <b className="font-mono-nums" data-testid="shared-card-count">{count}</b></span>
        <ChevronLeft className={`h-4 w-4 transition-transform ${open ? "-rotate-90" : ""}`} />
      </button>
      {open && (
        <div className="rounded-lg border border-slate-200 bg-white p-2 space-y-1" data-testid="shared-card-list">
          {sharers.length === 0 ? (
            <p className="text-[11px] text-slate-400 px-1 py-1">لا يوجد اشتراك آخر بهذا الكرت بعد — سيُحتسب تلقائياً عند إدخال نفس الرقم لمركبة أخرى.</p>
          ) : (
            sharers.map((s) => (
              <div key={s.id} className="flex items-center justify-between gap-2 rounded-md px-2 py-1 text-xs hover:bg-slate-50">
                <span className="font-semibold text-slate-700 truncate">{s.current ? s.current.employee_name : "— بدون مستلم"}{s.id === currentVehicleId ? " (هذه المركبة)" : ""}</span>
                <span className="font-mono-nums text-slate-400 shrink-0" dir="ltr">{s.identifier || "—"}</span>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

// Compact shared-card badge for lists: shows the card number and, when shared by >1 vehicle,
// a clickable count that reveals the names of all riders on the same card.
const SharedCardBadge = ({ asset, assets }) => {
  const [open, setOpen] = useState(false);
  const num = normCard(asset.operating_card_number);
  const sharers = useMemo(
    () => (num ? (assets || []).filter((a) => normCard(a.operating_card_number) === num) : []),
    [num, assets]
  );
  if (!num) return null;
  const shared = sharers.length > 1;
  return (
    <span className="block mt-1" data-testid={`vehicle-card-badge-${asset.id}`}>
      <button type="button" onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); }}
        className={`inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[11px] font-semibold border ${shared ? "bg-amber-50 border-amber-200 text-amber-700" : "bg-slate-50 border-slate-200 text-slate-500"}`}
        data-testid={`vehicle-card-badge-toggle-${asset.id}`}>
        <CreditCard className="h-3 w-3" /> كرت: <span className="font-mono-nums" dir="ltr">{num}</span>
        {shared ? <span className="inline-flex items-center gap-0.5 rounded-full bg-amber-600 text-white px-1.5">مشترك ×{sharers.length}</span> : null}
      </button>
      {open && shared && (
        <div className="mt-1 rounded-lg border border-amber-200 bg-white p-1.5 shadow-sm space-y-0.5 w-max max-w-[260px]" data-testid={`vehicle-card-sharers-${asset.id}`}>
          <p className="text-[10px] text-slate-400 px-1">المناديب المشتركون في الكرت:</p>
          {sharers.map((s) => (
            <div key={s.id} className="flex items-center justify-between gap-2 rounded px-1.5 py-0.5 text-[11px] hover:bg-amber-50/60">
              <span className="font-semibold text-slate-700 truncate">{s.current ? s.current.employee_name : "— بدون مستلم"}</span>
              <span className="font-mono-nums text-slate-400 shrink-0" dir="ltr">{s.identifier || "—"}</span>
            </div>
          ))}
        </div>
      )}
    </span>
  );
};

const RegScanButton = ({ onRead, testid }) => {
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);
  const pick = async (e) => {
    const f = e.target.files?.[0]; e.target.value = "";
    if (!f) return;
    setBusy(true);
    try {
      const r = await api.uploadExtract(f, "استمارة");
      onRead(r);
      const parts = [r.plate_number && `اللوحة: ${r.plate_number}`, r.plate_number_en && `EN: ${r.plate_number_en}`, r.expiry_date && `الانتهاء: ${r.expiry_date}`].filter(Boolean);
      toast.success(parts.length ? `تمت قراءة الاستمارة (${parts.join(" · ")}) — راجع واضغط حفظ` : "تم رفع الاستمارة — لم تُقرأ اللوحة، أدخلها يدوياً");
    } catch { toast.error("تعذّر قراءة الاستمارة"); } finally { setBusy(false); }
  };
  return (
    <>
      <input ref={ref} type="file" accept="image/*,application/pdf" hidden onChange={pick} data-testid={`${testid}-input`} />
      <Button type="button" size="sm" variant="outline" disabled={busy} data-testid={testid} onClick={() => ref.current?.click()}
        className="w-full gap-1.5 border-emerald-200 text-emerald-700 hover:bg-emerald-50">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Camera className="h-4 w-4" />} قراءة الاستمارة (اللوحة عربي وإنجليزي + الانتهاء)
      </Button>
    </>
  );
};

const applyReg = (r) => (p) => ({
  ...p,
  identifier: r.plate_number || p.identifier,
  plate_en: r.plate_number_en || p.plate_en,
  registration_expiry: r.expiry_date || p.registration_expiry,
  registration_file_id: r.attachment_id || p.registration_file_id,
});

const PlateEnInput = ({ form, setForm, testid }) => (
  <div className="space-y-1.5">
    <Label>رقم اللوحة بالإنجليزي</Label>
    <Input data-testid={testid} dir="ltr" placeholder="6914 A L" value={form.plate_en || ""} onChange={(e) => setForm({ ...form, plate_en: e.target.value })} />
  </div>
);

const VehicleFormDialog = ({ open, onOpenChange, onSaved, typeOptions, branchOptions, employees, assets }) => {
  const { t, dir } = useLang();
  const [form, setForm] = useState({ type: "دراجة نارية", identifier: "", plate_en: "", branch: "", fuel_code: "", moto_kind: "صيني", operating_card_number: "", operating_card_expiry: "", inspection_expiry: "", registration_expiry: "", registration_file_id: null });
  const [assign, setAssign] = useState({ employee_id: "", received_at: today(), note: "" });
  const [saving, setSaving] = useState(false);
  const sigRef = useRef(null);

  useEffect(() => {
    if (open) {
      setForm({ type: typeOptions[0] || "دراجة نارية", identifier: "", plate_en: "", branch: "", fuel_code: "", moto_kind: "صيني", operating_card_number: "", operating_card_expiry: "", inspection_expiry: "", registration_expiry: "", registration_file_id: null });
      setAssign({ employee_id: "", received_at: today(), note: "" });
    }
  }, [open, typeOptions]);

  const save = async () => {
    if (!form.identifier.trim()) { toast.warning(t("dash.vehicles.plateWarn")); return; }
    setSaving(true);
    try {
      const payload = { ...form, identifier: form.identifier.trim(), moto_kind: isMoto(form.type) ? form.moto_kind : "" };
      if (assign.employee_id) {
        payload.employee_id = assign.employee_id;
        payload.received_at = assign.received_at;
        payload.note = assign.note;
        payload.signature_data = sigRef.current?.getSignature() || null;
      }
      await api.createAsset(payload);
      toast.success(t("dash.vehicles.added"));
      onOpenChange(false);
      onSaved();
    } catch { toast.error(t("dash.custody.toast.saveFail")); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg font-body max-h-[90vh] overflow-y-auto" dir={dir}>
        <DialogHeader className="text-start">
          <DialogTitle className="font-heading text-xl">{t("dash.vehicles.addTitle")}</DialogTitle>
          <DialogDescription className="text-start">{t("dash.vehicles.addDesc")}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-1">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>{t("dash.vehicles.type")}</Label>
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                <SelectTrigger data-testid="vehicle-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {typeOptions.map((ty) => <SelectItem key={ty} value={ty}>{ty}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="vehicle-plate">{t("dash.vehicles.plate")}</Label>
              <Input id="vehicle-plate" data-testid="vehicle-plate-input" dir="ltr" value={form.identifier}
                placeholder={t("dash.vehicles.platePh")} onChange={(e) => setForm({ ...form, identifier: e.target.value })} />
            </div>
          </div>
          <RegScanButton testid="vehicle-reg-scan" onRead={(r) => setForm(applyReg(r))} />
          <div className="grid grid-cols-2 gap-3">
            <PlateEnInput form={form} setForm={setForm} testid="vehicle-plate-en-input" />
            <div className="space-y-1.5">
              <Label>انتهاء الاستمارة</Label>
              <Input type="date" dir="ltr" data-testid="vehicle-reg-expiry" value={form.registration_expiry || ""} onChange={(e) => setForm({ ...form, registration_expiry: e.target.value })} />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="vehicle-branch">{t("dash.vehicles.branch")}</Label>
            <Input id="vehicle-branch" data-testid="vehicle-branch-input" list="vehicle-branches" value={form.branch}
              placeholder={t("dash.custody.form.branchPh")} onChange={(e) => setForm({ ...form, branch: e.target.value })} />
            <datalist id="vehicle-branches">{branchOptions.map((b) => <option key={b} value={b} />)}</datalist>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="vehicle-fuel-code">{t("dash.vehicles.fuelCode")}</Label>
            <Input id="vehicle-fuel-code" data-testid="vehicle-fuel-code-input" dir="ltr" value={form.fuel_code}
              placeholder={t("dash.vehicles.fuelCodePh")} onChange={(e) => setForm({ ...form, fuel_code: e.target.value })} />
            <p className="text-[11px] text-slate-400">{t("dash.vehicles.fuelCodeHint")}</p>
          </div>

          <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-3 space-y-3" data-testid="vehicle-card-block">
            <p className="text-sm font-bold text-slate-700">كرت التشغيل والفحص الدوري</p>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>رقم كرت التشغيل</Label>
                <Input data-testid="vehicle-opcard-number" dir="ltr" value={form.operating_card_number} onChange={(e) => setForm({ ...form, operating_card_number: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label>انتهاء كرت التشغيل</Label>
                <Input type="date" data-testid="vehicle-opcard-expiry" dir="ltr" value={form.operating_card_expiry} onChange={(e) => setForm({ ...form, operating_card_expiry: e.target.value })} />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>انتهاء الفحص الدوري</Label>
              <Input type="date" data-testid="vehicle-inspection-expiry" dir="ltr" value={form.inspection_expiry} onChange={(e) => setForm({ ...form, inspection_expiry: e.target.value })} />
            </div>
            <SharedCardField cardNumber={form.operating_card_number} assets={assets} currentVehicleId={null} />
          </div>

          {isMoto(form.type) && (
            <div className="space-y-1.5" data-testid="vehicle-moto-kind-block">
              <Label>نوع الدباب</Label>
              <Select value={form.moto_kind} onValueChange={(v) => setForm({ ...form, moto_kind: v })}>
                <SelectTrigger data-testid="vehicle-moto-kind-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {MOTO_KINDS.map((k) => <SelectItem key={k} value={k} data-testid={`vehicle-moto-kind-opt-${k}`}>{k}</SelectItem>)}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-slate-400">يُستخدم لحساب معيار استهلاك الوقود (صيني: 2 طلب/لتر · بوكسر: 1.5 طلب/لتر).</p>
            </div>
          )}

          <div className="rounded-xl border border-blue-100 bg-blue-50/40 p-3 space-y-3">
            <p className="text-sm font-semibold text-blue-800 flex items-center gap-1.5">
              <User className="h-4 w-4" /> {t("dash.vehicles.assignOptional")}
            </p>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>{t("dash.custody.form.employee")}</Label>
                <Select value={assign.employee_id} onValueChange={(v) => setAssign({ ...assign, employee_id: v })}>
                  <SelectTrigger data-testid="vehicle-initial-employee-select"><SelectValue placeholder={t("dash.custody.form.chooseEmpPh")} /></SelectTrigger>
                  <SelectContent>
                    {employees.filter(isActiveEmp).map((e) => <SelectItem key={e.id} value={e.id}>{e.name}{e.branch ? ` — ${e.branch}` : ""}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="vehicle-received">{t("dash.custody.form.receivedAt")}</Label>
                <Input id="vehicle-received" type="date" data-testid="vehicle-received-input" value={assign.received_at}
                  onChange={(e) => setAssign({ ...assign, received_at: e.target.value })} />
              </div>
            </div>
            {assign.employee_id && (
              <div className="space-y-1.5">
                <Label className="flex items-center gap-1.5"><PenLine className="h-3.5 w-3.5" /> {t("dash.custody.form.receiverSig")}</Label>
                <SignaturePad ref={sigRef} />
              </div>
            )}
          </div>
        </div>
        <DialogFooter className="flex-row-reverse gap-2 sm:justify-start">
          <Button data-testid="save-vehicle-button" disabled={saving}
            className="bg-blue-600 hover:bg-blue-700 gap-1.5" onClick={save}>
            {saving && <Loader2 className="h-4 w-4 animate-spin" />} {t("dash.custody.form.save")}
          </Button>
          <Button variant="outline" onClick={() => onOpenChange(false)}>{t("dash.custody.form.cancel")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ---------- Assign an available vehicle to a driver who has none ----------
const NoVehicleDialog = ({ open, onOpenChange, onSaved, noVehEmployees, availableVehicles }) => {
  const { t, dir } = useLang();
  const [empId, setEmpId] = useState("");
  const [assigningId, setAssigningId] = useState("");

  useEffect(() => { if (open) setEmpId(""); }, [open]);

  const assign = async (vehicle) => {
    if (!empId) { toast.warning(t("dash.custody.toast.chooseEmp")); return; }
    setAssigningId(vehicle.id);
    try {
      await api.assignAsset(vehicle.id, { employee_id: empId, received_at: today() });
      const emp = noVehEmployees.find((e) => e.id === empId);
      toast.success(t("dash.vehicles.assignedToEmp", { plate: vehicle.identifier || "—", name: emp?.name || "" }));
      onOpenChange(false);
      onSaved();
    } catch { toast.error(t("dash.custody.toast.assignFail")); }
    finally { setAssigningId(""); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg font-body max-h-[90vh] overflow-y-auto" dir={dir}>
        <DialogHeader className="text-start">
          <DialogTitle className="font-heading text-xl flex items-center gap-2"><User className="h-5 w-5 text-amber-600" /> {t("dash.vehicles.noVehTitle")}</DialogTitle>
          <DialogDescription className="text-start">{t("dash.vehicles.noVehDesc")}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-1">
          {noVehEmployees.some((e) => e.urgent) && (
            <div className="rounded-xl border border-red-200 bg-red-50/70 p-3" data-testid="novehicle-urgent-box">
              <p className="text-xs font-semibold text-red-700 mb-2 flex items-center gap-1.5">
                <AlertTriangle className="h-3.5 w-3.5" /> {t("dash.vehicles.urgentTitle")}
              </p>
              <div className="flex flex-wrap gap-1.5">
                {noVehEmployees.filter((e) => e.urgent).map((e) => (
                  <button key={e.id} type="button" data-testid={`urgent-emp-${e.id}`} onClick={() => setEmpId(e.id)}
                    className={`text-xs rounded-full px-2.5 py-1 border transition ${empId === e.id ? "bg-red-600 text-white border-red-600" : "bg-white text-red-700 border-red-300 hover:bg-red-100"}`}>
                    {e.name}{e.branch ? ` — ${e.branch}` : ""}
                  </button>
                ))}
              </div>
            </div>
          )}
          <div className="space-y-1.5">
            <Label>{t("dash.vehicles.noVehPickEmp")}</Label>
            <Select value={empId} onValueChange={setEmpId}>
              <SelectTrigger data-testid="novehicle-employee-select"><SelectValue placeholder={t("dash.custody.form.chooseEmpPh")} /></SelectTrigger>
              <SelectContent>
                {noVehEmployees.filter(isActiveEmp).map((e) => (
                  <SelectItem key={e.id} value={e.id}>
                    <span className="flex items-center gap-1.5">
                      {e.urgent && <span className="h-1.5 w-1.5 rounded-full bg-red-500 shrink-0" />}
                      {e.name}{e.branch ? ` — ${e.branch}` : ""}{e.urgent ? ` (${t("dash.vehicles.urgentTag")})` : ""}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <p className="text-xs font-semibold text-slate-500 mb-2">{t("dash.vehicles.availableVehicles")} ({availableVehicles.length})</p>
            {availableVehicles.length === 0 ? (
              <p className="text-sm text-slate-400" data-testid="novehicle-no-available">{t("dash.vehicles.noAvailable")}</p>
            ) : (
              <div className="space-y-2 max-h-72 overflow-y-auto" data-testid="novehicle-available-list">
                {availableVehicles.map((v) => {
                  const Icon = vIcon(v.type);
                  return (
                    <div key={v.id} data-testid={`available-vehicle-${v.id}`}
                      className="flex items-center gap-3 rounded-xl border border-slate-200 p-2.5 hover:border-emerald-300 transition">
                      <Icon className="h-4 w-4 text-emerald-600 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-slate-700 text-sm truncate" dir="ltr">{v.identifier || "—"}</p>
                        <p className="text-[11px] text-slate-400">{v.type}{v.fuel_code ? ` · ${t("dash.vehicles.code")}: ${v.fuel_code}` : ""}</p>
                      </div>
                      <Button size="sm" data-testid={`assign-available-${v.id}`} disabled={!empId || assigningId === v.id}
                        className="bg-emerald-600 hover:bg-emerald-700 gap-1" onClick={() => assign(v)}>
                        {assigningId === v.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ArrowRightLeft className="h-3.5 w-3.5" />} {t("dash.vehicles.assignBtn")}
                      </Button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ---------- Edit a vehicle (plate, type, code, branch, current employee) ----------
const EditVehicleDialog = ({ vehicle, onOpenChange, onSaved, typeOptions, branchOptions, employees, assets }) => {
  const { t, dir } = useLang();
  const [form, setForm] = useState({ type: "", identifier: "", plate_en: "", fuel_code: "", branch: "", moto_kind: "صيني", operating_card_number: "", operating_card_expiry: "", inspection_expiry: "", operating_card_file_id: null, registration_expiry: "", registration_file_id: null });
  const [empId, setEmpId] = useState("__none__");
  const [saving, setSaving] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [photoBusy, setPhotoBusy] = useState(false);
  const photoRef = useRef(null);

  useEffect(() => {
    if (vehicle) {
      setForm({ type: vehicle.type || "دراجة نارية", identifier: vehicle.identifier || "", fuel_code: vehicle.fuel_code || "", branch: vehicle.branch || "", moto_kind: vehicle.moto_kind || "صيني", operating_card_number: vehicle.operating_card_number || "", operating_card_expiry: vehicle.operating_card_expiry || "", inspection_expiry: vehicle.inspection_expiry || "", operating_card_file_id: vehicle.operating_card_file_id || null, plate_en: vehicle.plate_en || "", registration_expiry: vehicle.registration_expiry || "", registration_file_id: vehicle.registration_file_id || null });
      setEmpId(vehicle.current?.employee_id && vehicle.current.employee_id !== "__admin__" ? vehicle.current.employee_id : "__none__");
      setPhotos(vehicle.photos || []);
    }
  }, [vehicle]);

  const addPhotos = async (e) => {
    const files = Array.from(e.target.files || []);
    if (photoRef.current) photoRef.current.value = "";
    if (!files.length) return;
    setPhotoBusy(true);
    try {
      for (const f of files) {
        if (!f.type.startsWith("image/")) continue;
        const url = await fileToDataUrl(f);
        const entry = await api.addAssetPhoto(vehicle.id, url);
        setPhotos((p) => [...p, entry]);
      }
      toast.success("تم حفظ صورة المركبة");
      onSaved?.();
    } catch { toast.error("تعذّر رفع الصورة"); }
    finally { setPhotoBusy(false); }
  };
  const removePhoto = async (pid) => {
    try {
      await api.deleteAssetPhoto(vehicle.id, pid);
      setPhotos((p) => p.filter((x) => x.id !== pid));
      onSaved?.();
    } catch { toast.error("تعذّر حذف الصورة"); }
  };

  const save = async () => {
    if (!form.identifier.trim()) { toast.warning(t("dash.vehicles.plateWarn")); return; }
    setSaving(true);
    try {
      await api.updateAsset(vehicle.id, { type: form.type, identifier: form.identifier.trim(), fuel_code: form.fuel_code.trim(), branch: form.branch, moto_kind: isMoto(form.type) ? form.moto_kind : "", notes: vehicle.notes || "", operating_card_number: form.operating_card_number.trim(), operating_card_expiry: form.operating_card_expiry, inspection_expiry: form.inspection_expiry, operating_card_file_id: form.operating_card_file_id || null, plate_en: (form.plate_en || "").trim(), registration_expiry: form.registration_expiry || "", registration_file_id: form.registration_file_id || "" });
      const curId = vehicle.current?.employee_id && vehicle.current.employee_id !== "__admin__" ? vehicle.current.employee_id : "__none__";
      if (empId !== curId) {
        if (empId === "__none__") { if (vehicle.current) await api.returnAsset(vehicle.id, { returned_at: today() }); }
        else await api.assignAsset(vehicle.id, { employee_id: empId, received_at: today() });
      }
      toast.success(t("dash.vehicles.editDone"));
      onOpenChange(false); onSaved();
    } catch { toast.error(t("dash.custody.toast.saveFail")); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={Boolean(vehicle)} onOpenChange={(v) => !v && onOpenChange(false)}>
      <DialogContent className="sm:max-w-lg font-body max-h-[90vh] overflow-y-auto" dir={dir}>
        <DialogHeader className="text-start">
          <DialogTitle className="font-heading text-xl">{t("dash.vehicles.editTitle")}</DialogTitle>
          <DialogDescription className="text-start">{t("dash.vehicles.editDesc")}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-1">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>{t("dash.vehicles.type")}</Label>
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                <SelectTrigger data-testid="edit-vehicle-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>{typeOptions.map((ty) => <SelectItem key={ty} value={ty}>{ty}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>{t("dash.vehicles.plate")}</Label>
              <Input data-testid="edit-vehicle-plate-input" dir="ltr" value={form.identifier} onChange={(e) => setForm({ ...form, identifier: e.target.value })} />
            </div>
          </div>
          <RegScanButton testid="edit-vehicle-reg-scan" onRead={(r) => setForm(applyReg(r))} />
          <div className="grid grid-cols-2 gap-3">
            <PlateEnInput form={form} setForm={setForm} testid="edit-vehicle-plate-en-input" />
            <div className="space-y-1.5">
              <Label>انتهاء الاستمارة</Label>
              <Input type="date" dir="ltr" data-testid="edit-vehicle-reg-expiry" value={form.registration_expiry || ""} onChange={(e) => setForm({ ...form, registration_expiry: e.target.value })} />
            </div>
          </div>
          {form.registration_file_id && <a href={fileUrl(form.registration_file_id)} target="_blank" rel="noreferrer" data-testid="edit-vehicle-reg-file" className="inline-flex items-center gap-1 text-xs text-slate-600 hover:text-blue-600"><Eye className="h-3.5 w-3.5" /> معاينة الاستمارة</a>}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>{t("dash.vehicles.fuelCode")}</Label>
              <Input data-testid="edit-vehicle-fuel-code-input" dir="ltr" value={form.fuel_code} onChange={(e) => setForm({ ...form, fuel_code: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>{t("dash.vehicles.branch")}</Label>
              <Input data-testid="edit-vehicle-branch-input" list="edit-vehicle-branches" value={form.branch} onChange={(e) => setForm({ ...form, branch: e.target.value })} />
              <datalist id="edit-vehicle-branches">{branchOptions.map((b) => <option key={b} value={b} />)}</datalist>
            </div>
          </div>
          <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-3 space-y-3" data-testid="edit-vehicle-card-block">
            <p className="text-sm font-bold text-slate-700">كرت التشغيل والفحص الدوري</p>
            <p className="text-[11px] text-slate-400">تبقى هذه البيانات مرتبطة بالمركبة وتنتقل معها تلقائياً عند نقلها لموظف آخر.</p>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>رقم كرت التشغيل</Label>
                <Input data-testid="edit-vehicle-opcard-number" dir="ltr" value={form.operating_card_number} onChange={(e) => setForm({ ...form, operating_card_number: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label>انتهاء كرت التشغيل</Label>
                <Input type="date" data-testid="edit-vehicle-opcard-expiry" dir="ltr" value={form.operating_card_expiry} onChange={(e) => setForm({ ...form, operating_card_expiry: e.target.value })} />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>انتهاء الفحص الدوري</Label>
              <Input type="date" data-testid="edit-vehicle-inspection-expiry" dir="ltr" value={form.inspection_expiry} onChange={(e) => setForm({ ...form, inspection_expiry: e.target.value })} />
            </div>
            <SharedCardField cardNumber={form.operating_card_number} assets={assets} currentVehicleId={vehicle?.id} />
            <div className="space-y-1.5" data-testid="edit-vehicle-opcard-file">
              <Label className="text-xs">ملف كرت التشغيل (ينتقل للموظف عند الإسناد)</Label>
              <div className="flex items-center gap-2">
                <input type="file" accept="image/*,application/pdf" hidden id="edit-opcard-file-input" data-testid="edit-vehicle-opcard-file-input"
                  onChange={async (e) => {
                    const f = e.target.files?.[0]; e.target.value = "";
                    if (!f) return;
                    try {
                      const r = await api.uploadExtract(f, "كرت التشغيل");
                      setForm((p) => ({
                        ...p,
                        operating_card_file_id: r.attachment_id,
                        operating_card_expiry: r.expiry_date || p.operating_card_expiry,
                        operating_card_number: p.operating_card_number || (r.card_number || ""),
                      }));
                      const parts = [];
                      if (r.card_number) parts.push(`رقم: \u2066${r.card_number}\u2069`);
                      if (r.expiry_date) parts.push(`انتهاء: ${r.expiry_date}`);
                      toast.success(parts.length ? `تم رفع الملف وقراءته (${parts.join(" · ")}) — اضغط حفظ` : "تم رفع الملف — اضغط حفظ");
                    }
                    catch { toast.error("تعذّر رفع الملف"); }
                  }} />
                <Button type="button" size="sm" variant="outline" className="gap-1.5 border-blue-200 text-blue-700 hover:bg-blue-50"
                  onClick={() => document.getElementById("edit-opcard-file-input")?.click()}>
                  <Upload className="h-4 w-4" /> {form.operating_card_file_id ? "استبدال الملف" : "رفع ملف الكرت"}
                </Button>
                {form.operating_card_file_id && (
                  <a href={fileUrl(form.operating_card_file_id)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-slate-600 hover:text-blue-600"><Eye className="h-3.5 w-3.5" /> معاينة</a>
                )}
              </div>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>{t("dash.vehicles.currentEmployee")}</Label>
            <Select value={empId} onValueChange={setEmpId}>
              <SelectTrigger data-testid="edit-vehicle-employee-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">{t("dash.vehicles.noEmployee")}</SelectItem>
                {employees.filter(isActiveEmp).map((e) => <SelectItem key={e.id} value={e.id}>{e.name}{e.branch ? ` — ${e.branch}` : ""}</SelectItem>)}
              </SelectContent>
            </Select>
            <p className="text-[11px] text-slate-400">{t("dash.vehicles.editEmpHint")}</p>
          </div>
          <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-3 space-y-2" data-testid="vehicle-photos-section">
            <div className="flex items-center justify-between">
              <p className="text-sm font-bold text-slate-700 flex items-center gap-1.5"><Camera className="h-4 w-4 text-blue-600" /> صور المركبة</p>
              <input ref={photoRef} type="file" accept="image/*" capture="environment" multiple hidden data-testid="vehicle-photo-input" onChange={addPhotos} />
              <Button type="button" size="sm" variant="outline" data-testid="vehicle-photo-add-btn" disabled={photoBusy}
                className="gap-1.5 border-blue-200 text-blue-700 hover:bg-blue-50" onClick={() => photoRef.current?.click()}>
                {photoBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Camera className="h-4 w-4" />} تصوير / إضافة
              </Button>
            </div>
            {photos.length === 0 ? (
              <p className="text-[11px] text-slate-400">لا توجد صور بعد. التقط صوراً للمركبة لحفظها في بياناتها.</p>
            ) : (
              <div className="grid grid-cols-3 gap-2">
                {photos.map((p) => (
                  <div key={p.id} className="relative" data-testid={`vehicle-photo-${p.id}`}>
                    <a href={fileUrl(p.file_id)} target="_blank" rel="noreferrer">
                      <img src={fileUrl(p.file_id)} alt={p.caption || ""} className="h-20 w-full object-cover rounded-lg border border-slate-200" />
                    </a>
                    {p.caption && <span className="absolute bottom-0 inset-x-0 bg-black/50 text-white text-[9px] px-1 py-0.5 rounded-b-lg truncate">{p.caption}</span>}
                    <button type="button" data-testid={`vehicle-photo-del-${p.id}`} onClick={() => removePhoto(p.id)}
                      className="absolute -top-1.5 -right-1.5 h-5 w-5 rounded-full bg-rose-600 text-white grid place-items-center shadow"><X className="h-3 w-3" /></button>
                  </div>
                ))}
              </div>
            )}
          </div>
          {isMoto(form.type) && (
            <div className="space-y-1.5" data-testid="edit-vehicle-moto-kind-block">
              <Label>نوع الدباب</Label>
              <Select value={form.moto_kind} onValueChange={(v) => setForm({ ...form, moto_kind: v })}>
                <SelectTrigger data-testid="edit-vehicle-moto-kind-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {MOTO_KINDS.map((k) => <SelectItem key={k} value={k} data-testid={`edit-vehicle-moto-kind-opt-${k}`}>{k}</SelectItem>)}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-slate-400">يُستخدم لحساب معيار استهلاك الوقود (صيني: 2 طلب/لتر · بوكسر: 1.5 طلب/لتر).</p>
            </div>
          )}
        </div>
        <DialogFooter className="flex-row-reverse gap-2 sm:justify-start">
          <Button data-testid="save-edit-vehicle-button" disabled={saving} className="bg-blue-600 hover:bg-blue-700 gap-1.5" onClick={save}>
            {saving && <Loader2 className="h-4 w-4 animate-spin" />} {t("dash.custody.form.save")}
          </Button>
          <Button variant="outline" onClick={() => onOpenChange(false)}>{t("dash.custody.form.cancel")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ---------- Main panel ----------
export const VehiclesPanel = ({ employees = [], branchOptions = [], canEdit }) => {
  const { t, dir, lang } = useLang();
  const [assets, setAssets] = useState([]);
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [branch, setBranch] = useState("all");
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [vStatuses, setVStatuses] = useState([]);
  const [statusTarget, setStatusTarget] = useState(null);
  useEffect(() => { api.vehicleStatuses().then(setVStatuses).catch(() => {}); }, []);
  const [addOpen, setAddOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [assignTarget, setAssignTarget] = useState(null);
  const [returnTarget, setReturnTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importingTam, setImportingTam] = useState(false);
  const [noVehOpen, setNoVehOpen] = useState(false);
  const [selV, setSelV] = useState(() => new Set());
  const [histV, setHistV] = useState(null);
  const [vtRev, setVtRev] = useState(0);
  const togV = (id) => setSelV((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const fileRef = useRef(null);
  const tamRef = useRef(null);

  const onImportFile = async (e) => {
    const file = e.target.files?.[0]; e.target.value = "";
    if (!file) return;
    setImporting(true);
    try {
      const r = await api.importVehicles(file);
      toast.success(`أُضيف ${r.created} مركبة جديدة · حُدّث ${r.updated} موجودة (بدون تكرار) · رُبط ${r.linked}${r.unmatched ? ` · غير مطابق ${r.unmatched}` : ""}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || t("dash.vehicles.importFail")); }
    finally { setImporting(false); }
  };

  const onImportTam = async (e) => {
    const file = e.target.files?.[0]; e.target.value = "";
    if (!file) return;
    setImportingTam(true);
    try {
      const r = await api.importVehiclesTam(file);
      toast.success(`تقرير تم: أُضيف ${r.created} · حُدّث ${r.updated} (بدون تكرار) · ${r.with_expiry} برخصة سير`);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "تعذّر استيراد تقرير تم"); }
    finally { setImportingTam(false); }
  };

  const onExport = async () => {
    try {
      const blob = await api.exportVehicles();
      await saveBlob(blob, "vehicles.xlsx");
    } catch { toast.error(t("dash.vehicles.exportFail")); }
  };

  const onTemplate = async () => {
    try {
      const blob = await api.vehiclesImportTemplate();
      await saveBlob(blob, "vehicles_template.xlsx");
    } catch { toast.error(t("dash.vehicles.exportFail")); }
  };

  const [rentals, setRentals] = useState(null);
  const [rentPreset, setRentPreset] = useState(null);
  const unRent = async (fl) => {
    if (!window.confirm(`إرجاع ${fl.identifier || ""} لمركبات الشركة وإزالتها من أسطول الإيجار؟`)) return;
    try { await api.deleteRentalFleet(fl.id); toast.success("رجعت المركبة لمركبات الشركة"); load(); } catch (e) { toast.error(e?.response?.data?.detail || "تعذّر الإزالة"); }
  };
  const rentTag = (a, sfx = "") => {
    const fl = rentals?.[a.id];
    return fl ? <Badge data-testid={`vehicle-rent-tag${sfx}-${a.id}`} className={`rounded-full gap-1 ${fl.status === "rented" ? "bg-violet-100 text-violet-700 border-violet-200" : "bg-violet-50 text-violet-600 border-violet-200"}`}><KeyRound className="h-3 w-3" />{fl.status === "rented" ? `مؤجرة${fl.active_contract?.renter_name ? ` · ${fl.active_contract.renter_name}` : ""}` : "إيجار"}</Badge> : null;
  };
  const rentBtn = (a, cls, sfx = "") => {
    if (!canEdit || !rentals) return null;
    const fl = rentals[a.id];
    return <button title={fl ? "إرجاع لمركبات الشركة" : "تحويل لأسطول الإيجار"} data-testid={`rent-vehicle${sfx}-${a.id}`} onClick={() => (fl ? unRent(fl) : setRentPreset({ id: a.id, identifier: a.identifier, type: a.type }))}
      className={`inline-flex items-center gap-1 whitespace-nowrap rounded-full border px-2 py-1 text-[11px] font-semibold transition ${fl ? "border-sky-200 text-sky-700 hover:bg-sky-50" : "border-violet-200 text-violet-700 hover:bg-violet-50"}`}>
      {fl ? <Building2 className="h-3.5 w-3.5" /> : <KeyRound className="h-3.5 w-3.5" />}{fl ? "للشركة" : "للإيجار"}</button>;
  };
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [a, ty, vt] = await Promise.all([api.custodyAssets(branch), api.custodyTypes(), api.vehicleTypes().catch(() => null)]);
      if (vt) registerVehicleTypes(vt.all || []);
      setAssets(a.filter((x) => isVehicleType(x.type)));
      api.rentalFleet().then((f) => setRentals(Object.fromEntries(f.map((x) => [x.vehicle_id, x])))).catch(() => setRentals(null));
      setTypes(ty);
    } catch { toast.error(t("dash.vehicles.loadFail")); }
    finally { setLoading(false); }
  }, [branch, t]);

  useEffect(() => { load(); }, [load]);

  const typeOptions = useMemo(() => {
    const fromTypes = types.map((x) => x.name).filter(isVehicleType);
    const set = new Set([...getVehicleTypes(), ...fromTypes]);
    return Array.from(set);
  }, [types, vtRev]); // eslint-disable-line

  const doReturn = async () => {
    try {
      await api.returnAsset(returnTarget.id, { returned_at: today() });
      toast.success(t("dash.vehicles.returned"));
      setReturnTarget(null); load();
    } catch { toast.error(t("dash.custody.toast.returnFail")); }
  };
  const doDelete = async () => {
    try {
      await api.deleteAsset(deleteTarget.id);
      toast.success(t("dash.vehicles.deleted"));
      setDeleteTarget(null); load();
    } catch { toast.error(t("dash.custody.toast.deleteFail")); }
  };

  const isAdminHeld = (a) => a.current && a.current.employee_id === "__admin__";
  const isAssigned = (a) => !!a.current && !isAdminHeld(a);
  const isBlocked = (a) => !!a.op_status?.blocking;
  const tryAssign = (a) => (isBlocked(a) ? toast.error(`لا يمكن تسليم المركبة وحالتها «${a.op_status.label}» — غيّر الحالة إلى تلقائي أولاً`) : setAssignTarget(a));
  const assigned = assets.filter(isAssigned).length;
  const availableVehicles = assets.filter((a) => !isAssigned(a) && !isBlocked(a));
  const NO_VEH = "بدون مركبة";
  const noVehEmployees = employees
    .filter((e) => !e.vehicle_number || !String(e.vehicle_number).trim())
    .map((e) => ({ ...e, urgent: e.employee_status === NO_VEH }))
    .sort((a, b) => (b.urgent === a.urgent ? 0 : b.urgent ? 1 : -1));
  const urgentCount = noVehEmployees.filter((e) => e.urgent).length;

  const L = {
    searchPh: { ar: "بحث برقم اللوحة أو الكود…", en: "Search by plate or code…", ur: "پلیٹ یا کوڈ سے تلاش…" },
    all: { ar: "كل الحالات", en: "All statuses", ur: "تمام حالتیں" },
    noResults: { ar: "لا توجد مركبات مطابقة للبحث", en: "No vehicles match your search", ur: "تلاش سے کوئی گاڑی مطابقت نہیں رکھتی" },
    importTam: { ar: "استيراد من تم", en: "Import from Tam", ur: "تم سے درآمد" },
    colRegExpiry: { ar: "انتهاء رخصة السير", en: "Registration expiry", ur: "لائسنس ختم" },
    regExpired: { ar: "منتهية", en: "Expired", ur: "ختم شدہ" },
    regSoon: { ar: "قريبة الانتهاء", en: "Expiring soon", ur: "جلد ختم" },
    regValid: { ar: "سارية", en: "Valid", ur: "درست" },
    regBanner: { ar: "رخص سير تحتاج تجديد", en: "Registrations needing renewal", ur: "تجدید درکار" },
  };
  const tl = (k) => L[k][lang] || L[k].ar;
  const q = query.trim().toLowerCase();
  const filtered = assets.filter((a) => {
    const matchesQ = !q
      || String(a.identifier || "").toLowerCase().includes(q)
      || String(a.fuel_code || "").toLowerCase().includes(q)
      || String(a.plate_en || "").toLowerCase().replace(/\s/g, "").includes(q.replace(/\s/g, ""));
    const st = isAssigned(a) ? "assigned" : "available";
    const fl = rentals?.[a.id];
    const matchesS = statusFilter === "all" || (statusFilter === st && !a.op_status) || statusFilter === `op:${a.op_status?.key}`
      || (statusFilter === "rental" && !!fl) || (statusFilter === "company" && !fl) || (statusFilter === "rented" && fl?.status === "rented") || (statusFilter === "rental_free" && fl && fl.status !== "rented");
    return matchesQ && matchesS && (typeFilter === "all" || (a.type || "").trim() === typeFilter);
  });

  const regAlerts = assets.filter((a) => {
    const i = regExpiryInfo(a.registration_expiry);
    return i && i.state !== "valid";
  });
  const regBadge = (a) => {
    const info = regExpiryInfo(a.registration_expiry);
    if (!info) return <span className="text-slate-300">—</span>;
    const cls = info.state === "expired" ? "bg-red-50 text-red-700 border-red-200"
      : info.state === "soon" ? "bg-amber-50 text-amber-700 border-amber-200"
      : "bg-emerald-50 text-emerald-700 border-emerald-200";
    const label = info.state === "expired" ? tl("regExpired") : info.state === "soon" ? tl("regSoon") : tl("regValid");
    return (
      <div className="flex flex-col gap-0.5" data-testid={`vehicle-reg-${a.id}`}>
        <span className="text-xs text-slate-700" dir="ltr">{info.iso}</span>
        <Badge className={`${cls} rounded-full text-[10px] w-fit`}>
          {label}{info.state !== "valid" ? ` · ${Math.abs(info.days)}ي` : ""}
        </Badge>
      </div>
    );
  };

  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
      className="space-y-5" data-testid="vehicles-panel">
      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <button type="button" data-testid="vehicles-kpi-total" onClick={() => setStatusFilter("all")}
          className={`text-start rounded-2xl border p-4 transition hover:shadow-md cursor-pointer group ${statusFilter === "all" ? "border-slate-400 ring-2 ring-slate-200 bg-slate-50" : "border-slate-200 bg-white hover:border-slate-300"}`}>
          <p className="text-xs text-slate-500 flex items-center gap-1">{t("dash.vehicles.kpiTotal")}
            <ChevronLeft className="h-3 w-3 opacity-0 group-hover:opacity-100 transition" /></p>
          <p className="font-heading text-2xl font-bold text-slate-800">{assets.length}</p>
        </button>
        <button type="button" data-testid="vehicles-kpi-assigned" onClick={() => setStatusFilter((s) => (s === "assigned" ? "all" : "assigned"))}
          className={`text-start rounded-2xl border p-4 transition hover:shadow-md cursor-pointer group ${statusFilter === "assigned" ? "border-blue-400 ring-2 ring-blue-200 bg-blue-50/60" : "border-slate-200 bg-white hover:border-blue-300"}`}>
          <p className="text-xs text-slate-500 flex items-center gap-1">{t("dash.vehicles.kpiAssigned")}
            <ChevronLeft className="h-3 w-3 opacity-0 group-hover:opacity-100 transition" /></p>
          <p className="font-heading text-2xl font-bold text-blue-700">{assigned}</p>
        </button>
        <button type="button" data-testid="vehicles-kpi-available" onClick={() => setStatusFilter((s) => (s === "available" ? "all" : "available"))}
          className={`text-start rounded-2xl border p-4 transition hover:shadow-md cursor-pointer group ${statusFilter === "available" ? "border-emerald-400 ring-2 ring-emerald-200 bg-emerald-50/60" : "border-slate-200 bg-white hover:border-emerald-300"}`}>
          <p className="text-xs text-slate-500 flex items-center gap-1">{t("dash.vehicles.kpiAvailable")}
            <ChevronLeft className="h-3 w-3 opacity-0 group-hover:opacity-100 transition" /></p>
          <p className="font-heading text-2xl font-bold text-emerald-600">{assets.filter((a) => !isAssigned(a) && !isBlocked(a)).length}</p>
        </button>
        <button type="button" data-testid="no-vehicle-card" onClick={() => setNoVehOpen(true)}
          className={`text-start rounded-2xl border p-4 transition hover:shadow-md cursor-pointer group ${urgentCount > 0 ? "border-red-200 bg-red-50/60 hover:border-red-400 hover:shadow-red-100/60" : "border-amber-200 bg-amber-50/60 hover:border-amber-400 hover:shadow-amber-100/60"}`}>
          <p className={`text-xs flex items-center gap-1 ${urgentCount > 0 ? "text-red-700" : "text-amber-700"}`}>{t("dash.vehicles.noVehCard")}
            <ChevronLeft className="h-3 w-3 opacity-0 group-hover:opacity-100 transition" /></p>
          <p className={`font-heading text-2xl font-bold ${urgentCount > 0 ? "text-red-700" : "text-amber-700"}`}>
            {noVehEmployees.length}
            {urgentCount > 0 && <span className="text-xs font-semibold text-red-600 ms-2" data-testid="no-vehicle-urgent-count">· {t("dash.vehicles.urgentCount", { n: urgentCount })}</span>}
          </p>
        </button>
      </div>
      <div className="grid grid-cols-2 gap-3" data-testid="vehicles-category-kpis">
        {[["company", "مركبات الشركة", assets.filter((a) => !rentals?.[a.id]).length, "border-sky-400 ring-sky-200 bg-sky-50/60", "text-sky-700", Building2],
          ["rental", "مركبات معدّة للإيجار", assets.filter((a) => !!rentals?.[a.id]).length, "border-violet-400 ring-violet-200 bg-violet-50/60", "text-violet-700", KeyRound]].map(([k, label, n, on, tx, Ic]) => (
          <button key={k} type="button" data-testid={`vehicles-kpi-cat-${k}`} onClick={() => setStatusFilter((s) => (s === k ? "all" : k))}
            className={`group flex items-center gap-3 rounded-2xl border p-4 text-start transition hover:shadow-md ${statusFilter === k ? `${on} ring-2` : "border-slate-200 bg-white hover:border-slate-300"}`}>
            <span className={`flex h-10 w-10 items-center justify-center rounded-xl bg-white shadow-sm ${tx}`}><Ic className="h-5 w-5" /></span>
            <span className="min-w-0"><span className="flex items-center gap-1 text-xs text-slate-500">{label}<ChevronLeft className="h-3 w-3 opacity-0 transition group-hover:opacity-100" /></span>
              <span className={`block font-heading text-2xl font-bold ${tx}`}>{n}</span></span>
          </button>))}
      </div>
      {vStatuses.some((x) => assets.some((a) => a.op_status?.key === x.key)) && (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6" data-testid="vehicles-status-kpis">
          {vStatuses.map((x) => ({ x, n: assets.filter((a) => a.op_status?.key === x.key).length })).filter(({ n }) => n > 0).map(({ x, n }) => {
            const on = statusFilter === `op:${x.key}`;
            return (
              <button key={x.key} type="button" data-testid={`vehicles-kpi-op-${x.key}`} onClick={() => setStatusFilter(on ? "all" : `op:${x.key}`)}
                className={`group rounded-2xl border p-3 text-start transition hover:shadow-md ${ST_COLORS[x.color] || ST_COLORS.slate} ${on ? "ring-2 ring-offset-1 ring-slate-400" : ""}`}>
                <p className="flex items-center gap-1 text-xs font-medium">{x.label}<ChevronLeft className="h-3 w-3 opacity-0 transition group-hover:opacity-100" /></p>
                <p className="font-heading text-2xl font-bold">{n}</p>
              </button>);
          })}
        </div>)}

      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="text-sm text-slate-500">{t("dash.vehicles.hint")}</p>
        <div className="flex items-center gap-2 flex-wrap">
          {assets.length > 0 && (
            <>
              <div className="relative">
                <Input data-testid="vehicles-search-input" value={query} onChange={(e) => setQuery(e.target.value)}
                  placeholder={tl("searchPh")} className="h-9 w-56" dir={dir} />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger data-testid="vehicles-status-filter" className="w-40"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{tl("all")}</SelectItem>
                  <SelectItem value="assigned">{t("dash.vehicles.stAssigned")}</SelectItem>
                  <SelectItem value="available">{t("dash.vehicles.stAvailable")}</SelectItem>
                  {vStatuses.map((x) => <SelectItem key={x.key} value={`op:${x.key}`} data-testid={`vehicles-filter-op-${x.key}`}>{x.label} ({assets.filter((a) => a.op_status?.key === x.key).length})</SelectItem>)}
                  <SelectItem value="company" data-testid="vehicles-filter-company">مركبات الشركة</SelectItem>
                  {rentals && <SelectItem value="rental" data-testid="vehicles-filter-rental">مركبات الإيجار ({Object.keys(rentals).length})</SelectItem>}
                  {rentals && <SelectItem value="rented" data-testid="vehicles-filter-rented">مؤجرة حالياً</SelectItem>}
                  {rentals && <SelectItem value="rental_free" data-testid="vehicles-filter-rental-free">إيجار · متاحة</SelectItem>}
                </SelectContent>
              </Select>
              <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger data-testid="vehicles-type-filter" className="w-40"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">كل الأنواع</SelectItem>
                  {Array.from(new Set([...typeOptions, ...assets.map((a) => (a.type || "").trim()).filter(Boolean)])).map((ty) => (
                    <SelectItem key={ty} value={ty} data-testid={`vehicles-type-opt-${ty}`}>{ty} ({assets.filter((a) => (a.type || "").trim() === ty).length})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </>
          )}
          <Select value={branch} onValueChange={setBranch}>
            <SelectTrigger data-testid="vehicles-branch-select" className="w-36"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("dash.custody.allBranches")}</SelectItem>
              {branchOptions.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}
            </SelectContent>
          </Select>
          {canEdit && (
            <>
              <input ref={fileRef} type="file" accept=".xlsx,.xls" className="hidden" data-testid="vehicles-import-input" onChange={onImportFile} />
              <input ref={tamRef} type="file" accept=".xlsx,.xls" className="hidden" data-testid="vehicles-import-tam-input" onChange={onImportTam} />
              <Button size="sm" variant="ghost" data-testid="template-vehicles-button" className="gap-1.5 text-slate-500" onClick={onTemplate}>
                <FileDown className="h-4 w-4" /> {t("dash.vehicles.template")}
              </Button>
              <Button size="sm" variant="outline" data-testid="import-vehicles-button" disabled={importing}
                className="gap-1.5" onClick={() => fileRef.current?.click()}>
                {importing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} {t("dash.vehicles.importExcel")}
              </Button>
              <Button size="sm" variant="outline" data-testid="import-vehicles-tam-button" disabled={importingTam}
                className="gap-1.5 border-emerald-300 text-emerald-700 hover:bg-emerald-50" onClick={() => tamRef.current?.click()}>
                {importingTam ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} {tl("importTam")}
              </Button>
              <BulkTools section="vehicles" merge onDone={load} />
              <VehicleTypesManager onChanged={() => { setVtRev((x) => x + 1); load(); }} />
              <VehicleStatusManager statuses={vStatuses} onChanged={setVStatuses} />
              <VehicleDowntimeReport />
              <Button size="sm" variant="outline" data-testid="export-vehicles-button" className="gap-1.5" onClick={onExport}>
                <Download className="h-4 w-4" /> {t("dash.vehicles.exportExcel")}
              </Button>
              <Button size="sm" data-testid="add-vehicle-button" className="bg-blue-600 hover:bg-blue-700 gap-1.5"
                onClick={() => setAddOpen(true)}>
                <Plus className="h-4 w-4" /> {t("dash.vehicles.addVehicle")}
              </Button>
            </>
          )}
        </div>
      </div>

      {regAlerts.length > 0 && (
        <div className="rounded-2xl border border-amber-200 bg-amber-50/70 p-3" data-testid="vehicles-reg-banner">
          <p className="text-sm font-semibold text-amber-800 flex items-center gap-1.5">
            <AlertTriangle className="h-4 w-4" /> {tl("regBanner")} ({regAlerts.length})
          </p>
          <div className="mt-1.5 flex flex-wrap gap-1.5">
            {regAlerts.slice(0, 12).map((a) => {
              const info = regExpiryInfo(a.registration_expiry);
              return (
                <span key={a.id} dir="ltr"
                  className={`text-[11px] rounded-full px-2 py-0.5 border ${info.state === "expired" ? "bg-red-50 text-red-700 border-red-200" : "bg-amber-100 text-amber-800 border-amber-300"}`}>
                  {a.identifier} · {info.iso}
                </span>
              );
            })}
            {regAlerts.length > 12 && <span className="text-[11px] text-amber-700">+{regAlerts.length - 12}</span>}
          </div>
        </div>
      )}

      {(() => {
        const opAlerts = assets.filter((a) => { const i = regExpiryInfo(a.operating_card_expiry); return i && i.state !== "valid"; });
        const inspAlerts = assets.filter((a) => { const i = regExpiryInfo(a.inspection_expiry); return i && i.state !== "valid"; });
        if (!opAlerts.length && !inspAlerts.length) return null;
        const chip = (a, iso, labelAr) => {
          const info = regExpiryInfo(iso);
          return (
            <span key={a.id + labelAr} dir="ltr"
              className={`text-[11px] rounded-full px-2 py-0.5 border ${info.state === "expired" ? "bg-red-50 text-red-700 border-red-200" : "bg-amber-100 text-amber-800 border-amber-300"}`}>
              {a.identifier} · {labelAr} {info.state === "expired" ? "منتهي" : "قريب"} · {info.iso}
            </span>
          );
        };
        return (
          <div className="rounded-2xl border border-red-200 bg-red-50/60 p-3" data-testid="vehicles-opcard-banner">
            <p className="text-sm font-semibold text-red-800 flex items-center gap-1.5">
              <AlertTriangle className="h-4 w-4" /> كروت تشغيل / فحص دوري تحتاج انتباهك ({opAlerts.length + inspAlerts.length})
            </p>
            <div className="mt-1.5 flex flex-wrap gap-1.5">
              {opAlerts.slice(0, 10).map((a) => chip(a, a.operating_card_expiry, "كرت التشغيل"))}
              {inspAlerts.slice(0, 10).map((a) => chip(a, a.inspection_expiry, "الفحص الدوري"))}
            </div>
          </div>
        );
      })()}

      {loading ? (
        <div className="py-16 text-center text-slate-400">{t("dash.custody.loading")}</div>
      ) : assets.length === 0 ? (
        <div className="py-16 text-center text-slate-400" data-testid="vehicles-empty">{t("dash.vehicles.empty")}</div>
      ) : filtered.length === 0 ? (
        <div className="py-16 text-center text-slate-400" data-testid="vehicles-no-results">{tl("noResults")}</div>
      ) : (
        <>
        {canEdit && <VehicleBulkBar selected={selV} setSelected={setSelV} visibleIds={filtered.map((a) => a.id)} onDone={load} />}
        <div className="sm:hidden space-y-2" data-testid="vehicles-cards">
          {filtered.map((a) => { const Icon = vIcon(a.type); return (
            <div key={a.id} data-testid={`vehicle-card-${a.id}`} className="rounded-2xl border border-slate-200 bg-white p-3">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex items-start gap-2">
                  {canEdit && <input type="checkbox" checked={selV.has(a.id)} onChange={() => togV(a.id)} data-testid={`vehicle-check-m-${a.id}`} className="mt-1 h-4 w-4 accent-sky-600" />}
                  <div className="min-w-0">
                  <p className="font-semibold text-slate-800" dir="ltr">{a.identifier || "—"}{a.plate_en ? <span className="ms-2 text-[11px] font-mono font-normal text-slate-500">{a.plate_en}</span> : null}</p>
                  {rentTag(a, "-m")}
                  <p className="text-xs text-slate-500 inline-flex items-center gap-1"><Icon className="h-3.5 w-3.5 text-blue-600" /> {a.type}{a.branch ? ` · ${a.branch}` : ""}</p>
                  </div>
                </div>
                <div className="shrink-0"><VehicleStatusBadge asset={a} canEdit={canEdit} onOpen={setStatusTarget} suffix="-m">
                {isAssigned(a)
                  ? <Badge className="bg-blue-50 text-blue-700 border-blue-200 rounded-full shrink-0">{t("dash.vehicles.stAssigned")}</Badge>
                  : <div className="flex items-center gap-1 shrink-0">
                      {isAdminHeld(a) && <Badge data-testid={`vehicle-admin-tag-${a.id}`} className="bg-slate-100 text-slate-600 border-slate-200 rounded-full gap-1"><Building2 className="h-3 w-3" />لدى الإدارة</Badge>}
                      <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 rounded-full gap-1"><CheckCircle2 className="h-3 w-3" />{t("dash.vehicles.stAvailable")}</Badge>
                    </div>}
                </VehicleStatusBadge></div>
              </div>
              <p className="mt-1 text-xs text-slate-500">{t("dash.vehicles.colHolder")}: <b className="text-slate-700">{a.current ? a.current.employee_name : "—"}</b></p>
              <SharedCardBadge asset={a} assets={assets} />
              {a.registration_expiry && (
                <div className="mt-1 text-xs text-slate-500 flex items-center gap-1.5">{tl("colRegExpiry")}: {regBadge(a)}</div>
              )}
              <div className="mt-2 flex items-center gap-1 border-t border-slate-100 pt-2">
                {canEdit && <button data-testid={`edit-vehicle-m-${a.id}`} onClick={() => setEditTarget(a)} className="p-2 rounded-lg text-slate-500 hover:bg-slate-100"><Pencil className="h-4 w-4" /></button>}
                {canEdit && <button data-testid={`assign-vehicle-m-${a.id}`} onClick={() => tryAssign(a)} className="p-2 rounded-lg text-blue-600 hover:bg-blue-50"><ArrowRightLeft className="h-4 w-4" /></button>}
                {canEdit && a.current && <button data-testid={`return-vehicle-m-${a.id}`} onClick={() => setReturnTarget(a)} className="p-2 rounded-lg text-amber-600 hover:bg-amber-50"><Undo2 className="h-4 w-4" /></button>}
                {a.current && <button data-testid={`receipt-vehicle-m-${a.id}`} onClick={() => api.getCompany().catch(() => ({})).then((co) => printVehicleReceipt(a, { ...a.current, iqama_number: (employees.find((e) => e.id === a.current.employee_id) || {}).iqama_number || "" }, co))} className="p-2 rounded-lg text-slate-600 hover:bg-slate-100"><Printer className="h-4 w-4" /></button>}
                <button data-testid={`history-vehicle-m-${a.id}`} title="كشف التنقل" onClick={() => setHistV(a)} className="p-2 rounded-lg text-indigo-600 hover:bg-indigo-50"><History className="h-4 w-4" /></button>
                {rentBtn(a, "p-2", "-m")}
                {canEdit && <button data-testid={`delete-vehicle-m-${a.id}`} onClick={() => setDeleteTarget(a)} className="p-2 rounded-lg text-red-600 hover:bg-red-50"><Trash2 className="h-4 w-4" /></button>}
              </div>
            </div>
          ); })}
        </div>
        <div className="hidden sm:block overflow-x-auto rounded-2xl border border-slate-200 bg-white">
          <table className="w-full text-sm min-w-[940px]" data-testid="vehicles-table">
            <thead>
              <tr className="border-b border-slate-100 text-slate-500">
                {canEdit && <th className="p-3 w-8"><input type="checkbox" data-testid="vehicles-check-all" className="h-4 w-4 accent-sky-600"
                  checked={filtered.length > 0 && filtered.every((x) => selV.has(x.id))} onChange={(e) => setSelV(e.target.checked ? new Set(filtered.map((x) => x.id)) : new Set())} /></th>}
                <th className="p-3 text-start font-semibold">{t("dash.vehicles.colPlate")}</th>
                <th className="p-3 text-start font-semibold">{t("dash.vehicles.colType")}</th>
                <th className="p-3 text-start font-semibold">{t("dash.vehicles.colHolder")}</th>
                <th className="p-3 text-start font-semibold">{t("dash.vehicles.colBranch")}</th>
                <th className="p-3 text-start font-semibold">{tl("colRegExpiry")}</th>
                <th className="p-3 text-start font-semibold">{t("dash.vehicles.colStatus")}</th>
                <th className="p-3 text-end font-semibold">{t("dash.vehicles.colActions")}</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((a) => {
                const Icon = vIcon(a.type);
                return (
                  <tr key={a.id} data-testid={`vehicle-row-${a.id}`} className="border-b border-slate-50 last:border-0 hover:bg-slate-50/60">
                    {canEdit && <td className="p-3"><input type="checkbox" checked={selV.has(a.id)} onChange={() => togV(a.id)} data-testid={`vehicle-check-${a.id}`} className="h-4 w-4 accent-sky-600" /></td>}
                    <td className="p-3 font-semibold text-slate-800" dir="ltr">
                      {a.identifier || "—"}
                      {a.plate_en ? <span className="block text-[11px] font-mono font-normal text-slate-500" data-testid={`vehicle-plate-en-${a.id}`}>{a.plate_en}</span> : null}
                      {a.fuel_code ? <span className="block text-[11px] font-normal text-slate-400" data-testid={`vehicle-code-${a.id}`}>{t("dash.vehicles.code")}: {a.fuel_code}</span> : null}
                      <SharedCardBadge asset={a} assets={assets} />
                    </td>
                    <td className="p-3">
                      <span className="inline-flex items-center gap-1.5 text-slate-700"><Icon className="h-4 w-4 text-blue-600" /> {a.type}</span>
                      {rentals?.[a.id] && <span className="block mt-1">{rentTag(a)}</span>}
                    </td>
                    <td className="p-3 text-slate-700">{a.current ? a.current.employee_name : <span className="text-slate-400">—</span>}</td>
                    <td className="p-3 text-slate-500">{a.branch || "—"}</td>
                    <td className="p-3">{regBadge(a)}</td>
                    <td className="p-3">
                      <VehicleStatusBadge asset={a} canEdit={canEdit} onOpen={setStatusTarget}>
                      {isAssigned(a)
                        ? <Badge className="bg-blue-50 text-blue-700 border-blue-200 rounded-full">{t("dash.vehicles.stAssigned")}</Badge>
                        : <div className="flex items-center gap-1">
                            {isAdminHeld(a) && <Badge data-testid={`vehicle-admin-tag-row-${a.id}`} className="bg-slate-100 text-slate-600 border-slate-200 rounded-full gap-1"><Building2 className="h-3 w-3" />لدى الإدارة</Badge>}
                            <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 rounded-full gap-1"><CheckCircle2 className="h-3 w-3" />{t("dash.vehicles.stAvailable")}</Badge>
                          </div>}
                      </VehicleStatusBadge>
                      {a.op_status && a.current && !isAdminHeld(a) && <span className="mt-1 block text-[10px] text-amber-700" data-testid={`vehicle-status-holder-${a.id}`}>مع {a.current.employee_name}</span>}
                    </td>
                    <td className="p-3">
                      <div className="flex items-center justify-end gap-0.5">
                        {canEdit && (
                          <button title={t("dash.vehicles.editTitle")} data-testid={`edit-vehicle-${a.id}`} onClick={() => setEditTarget(a)}
                            className="p-1.5 rounded-lg text-slate-400 hover:text-slate-800 hover:bg-slate-100 transition"><Pencil className="h-4 w-4" /></button>
                        )}
                        {canEdit && (
                          <button title={t("dash.vehicles.assignTitle")} data-testid={`assign-vehicle-${a.id}`} onClick={() => tryAssign(a)}
                            className="p-1.5 rounded-lg text-slate-400 hover:text-blue-700 hover:bg-blue-50 transition"><ArrowRightLeft className="h-4 w-4" /></button>
                        )}
                        {canEdit && a.current && (
                          <button title={t("dash.custody.titles.ret")} data-testid={`return-vehicle-${a.id}`} onClick={() => setReturnTarget(a)}
                            className="p-1.5 rounded-lg text-slate-400 hover:text-amber-700 hover:bg-amber-50 transition"><Undo2 className="h-4 w-4" /></button>
                        )}
                        {a.current && (
                          <button title={t("dash.vehicles.receipt")} data-testid={`receipt-vehicle-${a.id}`} onClick={() => api.getCompany().catch(() => ({})).then((co) => printVehicleReceipt(a, { ...a.current, iqama_number: (employees.find((e) => e.id === a.current.employee_id) || {}).iqama_number || "" }, co))}
                            className="p-1.5 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition"><Printer className="h-4 w-4" /></button>
                        )}
                        {canEdit && a.op_status?.key !== "workshop" && (
                          <button title="إرسال للورشة" data-testid={`workshop-vehicle-${a.id}`} onClick={() => setStatusTarget({ ...a, _preset: "workshop" })}
                            className="p-1.5 rounded-lg text-slate-400 hover:text-orange-700 hover:bg-orange-50 transition"><Wrench className="h-4 w-4" /></button>
                        )}
                        <button title="كشف التنقل" data-testid={`history-vehicle-${a.id}`} onClick={() => setHistV(a)}
                          className="p-1.5 rounded-lg text-slate-400 hover:text-indigo-700 hover:bg-indigo-50 transition"><History className="h-4 w-4" /></button>
                        {rentBtn(a, "p-1.5")}
                        {canEdit && (
                          <button title={t("dash.custody.titles.del")} data-testid={`delete-vehicle-${a.id}`} onClick={() => setDeleteTarget(a)}
                            className="p-1.5 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 transition"><Trash2 className="h-4 w-4" /></button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        </>
      )}

      <VehicleStatusDialog asset={statusTarget} statuses={vStatuses} onClose={() => setStatusTarget(null)} onSaved={load} />

      <VehicleHistoryDialog vehicle={histV} onClose={() => setHistV(null)} />
      <VehicleFormDialog open={addOpen} onOpenChange={setAddOpen} onSaved={load}
        typeOptions={typeOptions} branchOptions={branchOptions} employees={employees} assets={assets} />
      <NoVehicleDialog open={noVehOpen} onOpenChange={setNoVehOpen} onSaved={load}
        noVehEmployees={noVehEmployees} availableVehicles={availableVehicles} />
      <AssignAssetDialog asset={assignTarget} onOpenChange={() => setAssignTarget(null)} onSaved={load} employees={employees} />
      <FleetDialog open={!!rentPreset} preset={rentPreset} editing={null} onOpenChange={(o) => !o && setRentPreset(null)} onSaved={() => { setRentPreset(null); load(); }} />
      <EditVehicleDialog vehicle={editTarget} onOpenChange={() => setEditTarget(null)} onSaved={load}
        typeOptions={typeOptions} branchOptions={branchOptions} employees={employees} assets={assets} />

      <AlertDialog open={Boolean(returnTarget)} onOpenChange={(v) => !v && setReturnTarget(null)}>
        <AlertDialogContent dir={dir} className="font-body">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading text-start">{t("dash.vehicles.returnTitle")}</AlertDialogTitle>
            <AlertDialogDescription className="text-start">
              {t("dash.vehicles.returnDesc", { plate: returnTarget?.identifier || "", name: returnTarget?.current?.employee_name || "" })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-row-reverse gap-2 sm:justify-start">
            <AlertDialogAction data-testid="confirm-return-vehicle-button" onClick={doReturn} className="bg-blue-600 hover:bg-blue-700">{t("dash.custody.confirm.doReturn")}</AlertDialogAction>
            <AlertDialogCancel>{t("dash.custody.confirm.cancel")}</AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={Boolean(deleteTarget)} onOpenChange={(v) => !v && setDeleteTarget(null)}>
        <AlertDialogContent dir={dir} className="font-body">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading text-start">{t("dash.vehicles.deleteTitle")}</AlertDialogTitle>
            <AlertDialogDescription className="text-start">
              {t("dash.vehicles.deleteDesc", { plate: deleteTarget?.identifier || "" })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-row-reverse gap-2 sm:justify-start">
            <AlertDialogAction data-testid="confirm-delete-vehicle-button" onClick={doDelete} className="bg-red-600 hover:bg-red-700">{t("dash.custody.confirm.doDelete")}</AlertDialogAction>
            <AlertDialogCancel>{t("dash.custody.confirm.cancel")}</AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </motion.div>
  );
};
